import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Library, Calendar, Search, Download, ChevronLeft, ChevronRight,
  Image as ImageIcon, RefreshCw, X, Play, Pause, ZoomIn, ZoomOut,
  Check, Square, CheckSquare, Filter
} from 'lucide-react';
import api from '../services/api';

const LibraryPage = () => {
  // Filtros
  const [devices, setDevices]       = useState([]);
  const [deviceId, setDeviceId]     = useState('');
  const [searchDev, setSearchDev]   = useState('');
 const [channel, setChannel]   = useState('');     // vacío = nada seleccionado aún
const [hasFiltered, setHasFiltered] = useState(false);  // bandera de "ya aplicó"
  // Rango fechas (default: hoy completo)
 const todayLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const [startDate, setStartDate] = useState(todayLocal());
const [endDate, setEndDate]     = useState(todayLocal());

  // Datos
  const [timeline, setTimeline]     = useState([]); // buckets por hora
  const [snapshots, setSnapshots]   = useState([]); // listado paginado
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);

  // Vista
  const [zoomLevel, setZoomLevel]   = useState('day'); // day | 6h | hour
  const [selectedSnap, setSelectedSnap] = useState(null);
  const [selectedSet, setSelectedSet]   = useState(new Set()); // ids para export




// Cerca de tus useState, agrega:
const [appliedFilters, setAppliedFilters] = useState(null);

// Detectar si hay cambios sin aplicar
const filtersChanged = hasFiltered && appliedFilters && (
  appliedFilters.startDate !== startDate ||
  appliedFilters.endDate !== endDate ||
  appliedFilters.channel !== channel
);









  // Carga devices al inicio
  useEffect(() => {
    api.get('/api/devices').then(r => setDevices(r.data || []));
  }, []);

  const devicesFiltered = useMemo(() => {
    if (!searchDev) return devices;
    const q = searchDev.toLowerCase();
    return devices.filter(d =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.device_id || '').toLowerCase().includes(q)
    );
  }, [devices, searchDev]);

  const selectedDevice = devices.find(d => d.device_id === deviceId);

  // Carga timeline + summary cuando cambia device/fechas/canal
const loadData = useCallback(async () => {
  if (!deviceId || channel === '') return;
  setLoading(true);
  try {
    const startISO = `${startDate}T00:00:00`;
    const endISO   = `${endDate}T23:59:59`;
    const ch = (channel === 'all') ? '' : `&channel=${channel}`;

    const [tlRes, sumRes, snapsRes] = await Promise.all([
      api.get(`/api/library/timeline?device_id=${deviceId}&start=${startISO}&end=${endISO}${ch}`),
      api.get(`/api/library/summary?device_id=${deviceId}&start=${startISO}&end=${endISO}`),
      api.get(`/api/library/snapshots?device_id=${deviceId}&start=${startISO}&end=${endISO}${ch}&limit=500`)
    ]);
    setTimeline(tlRes.data.buckets || []);
    setSummary(sumRes.data);
    setSnapshots(snapsRes.data.snapshots || []);
  } catch (e) {
    console.error(e);
  } finally { setLoading(false); }
}, [deviceId, startDate, endDate, channel]);

// Solo carga cuando se aplica (no cuando cambian fechas/canal)
// Esto se controla solo desde el botón "Aplicar"


  // Selección
  const toggleSelect = (id) => {
    setSelectedSet(s => {
      const ns = new Set(s);
      ns.has(id) ? ns.delete(id) : ns.add(id);
      return ns;
    });
  };
  const selectAllVisible = () => setSelectedSet(new Set(snapshots.map(s => s.id)));
  const clearSelection   = () => setSelectedSet(new Set());

  const exportSelected = async () => {
    if (selectedSet.size === 0) return alert('Selecciona snapshots');
    if (selectedSet.size > 1000) return alert('Máximo 1000 por export');
    try {
      const r = await api.post('/api/library/export',
        { snapshot_ids: Array.from(selectedSet), device_id: deviceId },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `snapshots_${deviceId}_${Date.now()}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error: ' + e.message); }
  };

  // Presets fechas
const formatLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const setPreset = (preset) => {
  const t = new Date();
  let s = new Date(t);
  if (preset === 'today') {}
  else if (preset === 'yesterday') { s.setDate(s.getDate() - 1); }
  else if (preset === '7d')  { s.setDate(s.getDate() - 7); }
  else if (preset === '30d') { s.setDate(s.getDate() - 30); }

  if (preset === 'yesterday') {
    const y = formatLocal(s);
    setStartDate(y);
    setEndDate(y);
  } else {
    setStartDate(formatLocal(s));
    setEndDate(formatLocal(t));
  }
};

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Library size={26}/> Biblioteca de Snapshots</h1>
          <p style={S.subtitle}>
            {selectedDevice ? selectedDevice.name : 'Selecciona una unidad'} •
            {summary ? ` ${summary.total_snapshots || 0} snapshots • ${summary.tamano_mb || 0} MB` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedSet.size > 0 && (
            <button style={S.btnPrimary} onClick={exportSelected}>
              <Download size={16}/> Export {selectedSet.size} ZIP
            </button>
          )}
          <button style={S.btn} onClick={loadData}>
            <RefreshCw size={16} className={loading ? 'spin' : ''}/>
          </button>
        </div>
      </header>

      <div style={S.layout}>
        {/* Sidebar selección device */}
        <aside style={S.sidebar}>
          <div style={S.sidebarSearch}>
            <Search size={14}/>
            <input placeholder="Buscar unidad..." value={searchDev}
                   onChange={e => setSearchDev(e.target.value)}
                   style={S.searchInput}/>
          </div>
          <div style={S.deviceList}>
            {devicesFiltered.map(d => (
              <button key={d.id}
                     onClick={() => {
  setDeviceId(d.device_id);
  setHasFiltered(false);   // requiere aplicar filtros otra vez
  setSnapshots([]);
  setTimeline([]);
  setSummary(null);
  setSelectedSet(new Set());
}}

                      style={{
                        ...S.deviceItem,
                        background: deviceId === d.device_id ? '#dbeafe' : 'transparent',
                        borderLeft: deviceId === d.device_id ? '3px solid #3b82f6' : '3px solid transparent'
                      }}>
                <div style={{ fontWeight: 600 }}>{d.name}</div>
                <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                  {d.device_id}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <main style={S.main}>
        {!deviceId ? (
  <div style={S.empty}>
    <Library size={48} color="#9ca3af"/>
    <p>Selecciona una unidad de la lista para ver su biblioteca</p>
  </div>
) : (
  <>
    {/* Filtros */}
    <div style={S.filterBar}>
  <div style={S.filterGroup}>
    <Calendar size={16}/>
    <input type="date" value={startDate}
           onChange={e => setStartDate(e.target.value)}
           style={S.dateInput}/>
    <span>→</span>
    <input type="date" value={endDate}
           onChange={e => setEndDate(e.target.value)}
           style={S.dateInput}/>
  </div>

  <div style={S.presets}>
    <button style={S.preset} onClick={() => setPreset('today')}>Hoy</button>
    <button style={S.preset} onClick={() => setPreset('yesterday')}>Ayer</button>
    <button style={S.preset} onClick={() => setPreset('7d')}>7 días</button>
    <button style={S.preset} onClick={() => setPreset('30d')}>30 días</button>
  </div>


<button
  style={{
    ...S.btnPrimary,
    background: filtersChanged ? '#f59e0b' : '#3b82f6',  // ← naranja si pendiente
    opacity: channel === '' ? 0.5 : 1,
    cursor: channel === '' ? 'not-allowed' : 'pointer'
  }}
  disabled={channel === ''}
  onClick={() => {
    setHasFiltered(true);
    setAppliedFilters({ startDate, endDate, channel });
    loadData();
  }}
>
  <Search size={14}/>
  {filtersChanged ? 'Aplicar cambios' : (hasFiltered ? 'Aplicado' : 'Aplicar')}
</button>

{hasFiltered && (
  <button
    style={S.btn}
    onClick={() => {
      setHasFiltered(false);
      setAppliedFilters(null);
      setSnapshots([]);
      setTimeline([]);
      setSummary(null);
      setChannel('');
    }}
  >
    <X size={14}/> Limpiar
  </button>
)}
</div>

{/* Galería visual de canales */}
{deviceId && selectedDevice && (
  <div style={S.channelPickerSection}>
    <div style={S.channelPickerHeader}>
      <Filter size={14} color="#6b7280"/>
      <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
        Canal seleccionado:
      </span>
      <strong style={{ color: '#1f2937' }}>
        {channel === '' ? 'Ninguno' : channel === 'all' ? 'Todos' : `Canal ${channel}`}
      </strong>
      {/* Indicador estado */}
      <span style={{
        width: 10, height: 10, borderRadius: '50%',
        background: !hasFiltered ? '#9ca3af' :
                     filtersChanged ? '#f59e0b' :
                     '#10b981',
        marginLeft: 'auto',
      }} title={
        !hasFiltered ? 'Sin aplicar' :
        filtersChanged ? 'Cambios pendientes' :
        'Aplicado'
      }/>
    </div>

    <ChannelPicker
      deviceId={deviceId}
      totalChannels={selectedDevice.channels || 4}
      value={channel}
      onChange={setChannel}
    />
  </div>
)}






{filtersChanged && (
  <div style={{
    padding: '10px 14px',
    background: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: 6,
    color: '#92400e',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  }}>
    ⚠️ Tienes cambios en los filtros sin aplicar — pulsa <strong>Aplicar cambios</strong> para actualizar
  </div>
)}


    {!hasFiltered ? (
      <div style={S.empty}>
        <Filter size={48} color="#9ca3af"/>
        <p style={{ marginTop: 12, fontSize: 16, color: '#374151' }}>
          Selecciona canal y rango de fechas, luego pulsa <strong>Aplicar</strong>
        </p>
        <p style={{ fontSize: 13, color: '#6b7280' }}>
          Para optimizar la carga, recomendamos consultar 1 canal a la vez en rangos cortos.
        </p>
      </div>
    ) : (
      <>







              {/* Resumen por canal */}
              {summary && summary.por_canal && summary.por_canal.length > 0 && (
                <div style={S.kpiRow}>
                  {summary.por_canal.map(c => (
                    <div key={c.channel} style={S.kpi}>
                      <span style={S.kpiLabel}>Canal {c.channel}</span>
                      <span style={S.kpiValue}>{c.total}</span>
                      <span style={S.kpiUnit}>{c.mb} MB</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Timeline heatmap */}
              <Timeline buckets={timeline}
                        startDate={startDate}
                        endDate={endDate}
                        onSelect={(snapId) => {
                          api.get(`/api/devices/${deviceId}/snapshots?limit=1`).catch(()=>{});
                        }}/>

              {/* Grid de thumbnails */}
              <div style={S.gridSection}>
                <div style={S.gridHeader}>
                  <h3 style={{ margin: 0 }}>
                    Snapshots ({snapshots.length})
                    {selectedSet.size > 0 && <span style={S.selectedBadge}>{selectedSet.size} seleccionados</span>}
                  </h3>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={S.btnSmall} onClick={selectAllVisible}>
                      <CheckSquare size={14}/> Todos
                    </button>
                    <button style={S.btnSmall} onClick={clearSelection}>
                      <Square size={14}/> Ninguno
                    </button>
                  </div>
                </div>

                {snapshots.length === 0 ? (
                  <div style={S.empty}>
                    <ImageIcon size={32} color="#9ca3af"/>
                    <p>Sin snapshots en este rango</p>
                  </div>
                ) : (
                  <div style={S.grid}>
                    {snapshots.map(s => (
                      <SnapshotCard
                        key={s.id}
                        snap={s}
                        deviceId={deviceId}
                        selected={selectedSet.has(s.id)}
                        onToggle={() => toggleSelect(s.id)}
                        onView={() => setSelectedSnap(s)}
                      />
                    ))}
                  </div>
                )}
               </div>
      </>
    )}
  </>
)}
        </main>
      </div>

      {/* Modal preview */}
      {selectedSnap && (
        <SnapshotViewer
          deviceId={deviceId}
          snapshot={selectedSnap}
          onClose={() => setSelectedSnap(null)}
          onNav={(newSnap) => setSelectedSnap(newSnap)}
        />
      )}

      <style>{`
  .spin { animation: spin 1s linear infinite }
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes shimmer {
    0% { background-position: -200% 0 }
    100% { background-position: 200% 0 }
  }
`}</style>

    </div>
  );
};


// ============ Channel Picker con previews ============
const ChannelPicker = ({ deviceId, totalChannels, value, onChange }) => {
  return (
    <div style={S.channelPicker}>
      {/* Opción "Todos" */}
      <button
        type="button"
        onClick={() => onChange('all')}
        style={{
          ...S.channelTile,
          borderColor: value === 'all' ? '#3b82f6' : '#e5e7eb',
          background: value === 'all' ? '#eff6ff' : 'white',
        }}
      >
        <div style={S.channelTileImg}>
          <div style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: '#f3f4f6', color: '#6b7280', fontSize: 11, fontWeight: 600,
          }}>
            TODOS
          </div>
        </div>
        <div style={S.channelTileLabel}>
          {value === 'all' ? '✓ ' : ''}Todos
        </div>
      </button>

      {/* Una tile por canal */}
      {Array.from({ length: totalChannels }, (_, i) => i + 1).map(ch => (
        <ChannelTile
          key={ch}
          deviceId={deviceId}
          channel={ch}
          selected={value === ch}
          onClick={() => onChange(ch)}
        />
      ))}
    </div>
  );
};


const ChannelTile = ({ deviceId, channel, selected, onClick }) => {
  const [thumb, setThumb]   = useState(null);
  const [error, setError]   = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    let blobUrl = null;

    const loadThumb = async () => {
      setLoading(true);
      setError(false);
      try {
        // Pide última snapshot del canal — usamos el endpoint base64 existente
        const r = await api.get(
          `/api/devices/${deviceId}/snapshot/base64?channel=${channel}`
        );
        if (cancel) return;
        setThumb(r.data.image);  // base64 string
      } catch (e) {
        if (!cancel) setError(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    if (deviceId) loadThumb();
    return () => { cancel = true; };
  }, [deviceId, channel]);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...S.channelTile,
        borderColor: selected ? '#3b82f6' : '#e5e7eb',
        background: selected ? '#eff6ff' : 'white',
      }}
    >
      <div style={S.channelTileImg}>
        {loading ? (
          <div style={S.channelTileLoading}>
            <RefreshCw size={14} className="spin"/>
          </div>
        ) : error ? (
          <div style={S.channelTileError}>
            <ImageIcon size={20}/>
            <span style={{ fontSize: 9 }}>Sin preview</span>
          </div>
        ) : (
          <img src={`data:image/jpeg;base64,${thumb}`} alt="" style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%', objectFit: 'cover',
          }}/>
        )}
        <div style={S.channelTileBadge}>CH {channel}</div>
      </div>
      <div style={S.channelTileLabel}>
        {selected ? '✓ ' : ''}Canal {channel}
      </div>
    </button>
  );
};


// ============ Timeline Heatmap ============
const Timeline = ({ buckets, startDate, endDate }) => {
  if (!buckets || buckets.length === 0) {
    return <div style={S.timelineEmpty}>Sin datos en el rango</div>;
  }

  // Agrupar por canal
  const byChannel = {};
  let maxTotal = 0;
  buckets.forEach(b => {
    if (!byChannel[b.channel]) byChannel[b.channel] = [];
    byChannel[b.channel].push(b);
    if (b.total > maxTotal) maxTotal = b.total;
  });

  const channels = Object.keys(byChannel).sort((a, b) => +a - +b);

  // Generar todas las horas del rango
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T23:59:59');
  const hours = [];
  const cur = new Date(start);
  while (cur <= end) {
    hours.push(new Date(cur));
    cur.setHours(cur.getHours() + 1);
  }

  return (
    <div style={S.timelineSection}>
      <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#6b7280' }}>📊 Timeline (snapshots por hora)</h4>
      <div style={S.timelineWrap}>
        {channels.map(ch => (
          <div key={ch} style={S.timelineRow}>
            <div style={S.timelineLabel}>Ch {ch}</div>
            <div style={S.timelineBars}>
              {hours.map((h, i) => {
                const bucket = byChannel[ch].find(b => {
                  const bh = new Date(b.hora);
                  return bh.getTime() === h.getTime();
                });
                const total = bucket?.total || 0;
                const intensity = maxTotal > 0 ? total / maxTotal : 0;
                return (
                  <div key={i} style={{
                    ...S.timelineCell,
                    background: total === 0 ? '#f3f4f6' :
                                `rgba(59, 130, 246, ${0.2 + intensity * 0.8})`,
                  }} title={`${h.toLocaleString('es-MX')} - ${total} snapshots`}>
                    {total > 0 && intensity > 0.5 && (
                      <span style={S.timelineCount}>{total}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div style={S.timelineHourLabels}>
          <div style={S.timelineLabel}></div>
          <div style={{ ...S.timelineBars, justifyContent: 'space-between' }}>
            {[0, 6, 12, 18].map(h => (
              <span key={h} style={S.hourLabel}>{String(h).padStart(2,'0')}h</span>
            ))}
            <span style={S.hourLabel}>24h</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ SnapshotCard ============
const SnapshotCard = ({ snap, deviceId, selected, onToggle, onView }) => {
  const [thumbnail, setThumbnail] = useState(null);
  const [error, setError]         = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  // Observer: detecta cuando la card entra al viewport
  useEffect(() => {
    if (!cardRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();  // 1 sola vez, ya no observamos más
          }
        });
      },
      {
        root: null,
        rootMargin: '200px',  // empieza a cargar 200px antes de ser visible
        threshold: 0.01,
      }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  // Solo carga si es visible
  useEffect(() => {
    if (!isVisible) return;
    let cancel = false;
    let blobUrl = null;

    const loadThumb = async () => {
      try {
        // ⭐ Endpoint nuevo /thumb?w=320 — ~8KB en vez de 100KB
        const r = await api.get(`/api/snapshots/${snap.id}/thumb?w=320`, {
          responseType: 'blob'
        });
        if (cancel) return;
        blobUrl = URL.createObjectURL(r.data);
        setThumbnail(blobUrl);
      } catch (e) {
        if (!cancel) setError(true);
      }
    };
    loadThumb();
    return () => {
      cancel = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [isVisible, snap.id]);

  return (
    <div ref={cardRef} style={{
      ...S.snapCard,
      borderColor: selected ? '#3b82f6' : '#e5e7eb',
      background: selected ? '#eff6ff' : 'white',
    }}>
      <div style={S.snapCheckbox} onClick={onToggle}>
        {selected ? <CheckSquare size={18} color="#3b82f6"/> : <Square size={18} color="#9ca3af"/>}
      </div>
      <div style={S.snapImageWrap} onClick={onView}>
        {!isVisible ? (
          // Placeholder gris: aún no se ha hecho scroll hacia él
          <div style={S.snapPlaceholder}/>
        ) : thumbnail ? (
          <img src={thumbnail} alt="" style={S.snapImage} loading="lazy"/>
        ) : error ? (
          <div style={S.snapError}>⚠️</div>
        ) : (
          <div style={S.snapLoading}><RefreshCw size={16} className="spin"/></div>
        )}
        <div style={S.snapOverlay}>CH {snap.channel}</div>
      </div>
      <div style={S.snapMeta}>
        <div style={{ fontSize: 11, fontWeight: 500 }}>
          {new Date(snap.captured_at).toLocaleTimeString('es-MX')}
        </div>
        <div style={{ fontSize: 10, color: '#6b7280' }}>
          {(snap.image_size / 1024).toFixed(1)} KB
        </div>
      </div>
    </div>
  );
};


// ============ Snapshot Viewer (modal) ============
const SnapshotViewer = ({ deviceId, snapshot, onClose, onNav }) => {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/snapshots/${snapshot.id}`, { responseType: 'arraybuffer' });
      const base64 = btoa(new Uint8Array(r.data).reduce((d, b) => d + String.fromCharCode(b), ''));
      setImage(base64);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [snapshot.id]);

  useEffect(() => { load(); }, [load]);

  const navAdjacent = async (direction) => {
    try {
      const r = await api.get(`/api/library/snapshot/${snapshot.id}/adjacent?direction=${direction}`);
      onNav(r.data);
    } catch (e) {
      console.error('Sin más snapshots');
    }
  };

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  navAdjacent('prev');
      if (e.key === 'ArrowRight') navAdjacent('next');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.viewerModal} onClick={e => e.stopPropagation()}>
        <div style={S.viewerHeader}>
          <div>
            <strong>Canal {snapshot.channel}</strong>
            <span style={{ marginLeft: 12, color: '#6b7280' }}>
              {new Date(snapshot.captured_at).toLocaleString('es-MX')}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={S.btn} onClick={() => navAdjacent('prev')}>
              <ChevronLeft size={18}/>
            </button>
            <button style={S.btn} onClick={() => navAdjacent('next')}>
              <ChevronRight size={18}/>
            </button>
            <button style={S.btn} onClick={onClose}>
              <X size={18}/>
            </button>
          </div>
        </div>
        <div style={S.viewerImageWrap}>
          {loading ? (
            <RefreshCw size={32} className="spin" color="#9ca3af"/>
          ) : (
            <img src={`data:image/jpeg;base64,${image}`} alt="" style={S.viewerImage}/>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', marginTop: 8 }}>
          ← → para navegar • ESC para cerrar
        </div>
      </div>
    </div>
  );
};

// ============ Estilos ============
const S = {
  container: { padding: 20, maxWidth: 1600, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  btn: { padding: '8px 12px', background: 'white', border: '1px solid #e5e7eb',
         borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 },
  btnPrimary: { padding: '8px 14px', background: '#3b82f6', color: 'white',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 },
  btnSmall: { padding: '4px 10px', background: 'white', border: '1px solid #e5e7eb',
              borderRadius: 4, cursor: 'pointer', fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 4 },

  layout: { display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 },
  sidebar: { background: 'white', borderRadius: 8, overflow: 'hidden',
             boxShadow: '0 1px 2px rgba(0,0,0,0.05)', maxHeight: 'calc(100vh - 130px)',
             display: 'flex', flexDirection: 'column' },
  sidebarSearch: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px',
                   borderBottom: '1px solid #e5e7eb' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 13 },
  deviceList: { overflowY: 'auto', flex: 1 },
  deviceItem: { display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 12px', border: 'none', cursor: 'pointer',
                fontSize: 13, transition: 'background 0.1s' },

  main: { display: 'flex', flexDirection: 'column', gap: 14 },
  empty: { padding: 60, textAlign: 'center', color: '#6b7280',
           display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },

  filterBar: { display: 'flex', gap: 10, padding: 12, background: 'white',
               borderRadius: 8, alignItems: 'center', flexWrap: 'wrap',
               boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  dateInput: { padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13 },
  select: { padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13 },
  presets: { display: 'flex', gap: 4 },
  preset: { padding: '6px 10px', background: '#f3f4f6', border: 'none',
            borderRadius: 4, cursor: 'pointer', fontSize: 12 },

  kpiRow: { display: 'flex', gap: 8 },
  kpi: { background: 'white', padding: '10px 14px', borderRadius: 6,
         display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
         boxShadow: '0 1px 2px rgba(0,0,0,0.05)', minWidth: 90 },
  kpiLabel: { fontSize: 11, color: '#6b7280' },
  kpiValue: { fontSize: 18, fontWeight: 700, color: '#1f2937' },
  kpiUnit:  { fontSize: 10, color: '#9ca3af' },

  // Timeline
  timelineSection: { background: 'white', padding: 12, borderRadius: 8,
                     boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  timelineEmpty: { padding: 30, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8 },
  timelineWrap: { display: 'flex', flexDirection: 'column', gap: 4 },
  timelineRow: { display: 'flex', alignItems: 'center', gap: 8 },
  timelineLabel: { fontSize: 11, fontWeight: 600, width: 40, color: '#6b7280' },
  timelineBars: { flex: 1, display: 'flex', gap: 1, height: 28 },
  timelineCell: { flex: 1, borderRadius: 2, position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'transform 0.1s' },
  timelineCount: { fontSize: 9, color: 'white', fontWeight: 600 },
  timelineHourLabels: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 },
  hourLabel: { fontSize: 10, color: '#9ca3af' },

  // Grid
  gridSection: { background: 'white', padding: 14, borderRadius: 8,
                 boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  gridHeader: { display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 12 },
  selectedBadge: { marginLeft: 8, padding: '2px 8px', background: '#dbeafe',
                   color: '#1e40af', fontSize: 11, borderRadius: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 },

  snapCard: { border: '2px solid', borderRadius: 6, overflow: 'hidden',
              transition: 'all 0.15s', position: 'relative' },
  snapCheckbox: { position: 'absolute', top: 4, right: 4, zIndex: 2,
                  background: 'rgba(255,255,255,0.9)', borderRadius: 4,
                  padding: 2, cursor: 'pointer' },
snapImageWrap: {
  width: '100%',
  paddingTop: '56.25%',  // 16/9 = 0.5625 = 56.25% — fuerza el aspect ratio en TODOS los navegadores
  background: '#000',
  position: 'relative',
  cursor: 'pointer',
  overflow: 'hidden',
},
snapImage: {
  position: 'absolute',  // ← IMPORTANTE
  top: 0, left: 0,
  width: '100%', height: '100%',
  objectFit: 'cover'
},
  snapOverlay: { position: 'absolute', bottom: 4, left: 4,
                 background: 'rgba(0,0,0,0.7)', color: 'white',
                 padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600 },
snapLoading: {
  position: 'absolute',  // ← IMPORTANTE
  top: 0, left: 0,
  width: '100%', height: '100%',
  display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  color: '#9ca3af'
},
snapError: {
  position: 'absolute',  // ← IMPORTANTE
  top: 0, left: 0,
  width: '100%', height: '100%',
  display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  fontSize: 24
},

  snapMeta: { padding: '4px 8px' },

  // Viewer modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  viewerModal: { background: '#1f2937', borderRadius: 8, padding: 16,
                 maxWidth: '90vw', maxHeight: '90vh',
                 display: 'flex', flexDirection: 'column' },
  viewerHeader: { display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', color: 'white', marginBottom: 10 },
  viewerImageWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center',
                     minHeight: 400, minWidth: 600 },
  viewerImage: { maxWidth: '85vw', maxHeight: '75vh', objectFit: 'contain' },

snapPlaceholder: {
  position: 'absolute',  // ← IMPORTANTE
  top: 0, left: 0,
  width: '100%', height: '100%',
  background: 'linear-gradient(110deg, #1f2937 30%, #374151 50%, #1f2937 70%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
},

// === Channel picker (galería visual) ===
channelPickerSection: {
  background: 'white',
  padding: 14,
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
},
channelPickerHeader: {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 10,
  paddingBottom: 8,
  borderBottom: '1px solid #f3f4f6',
},
channelPicker: {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: 8,
},
channelTile: {
  border: '2px solid',
  borderRadius: 8,
  cursor: 'pointer',
  overflow: 'hidden',
  padding: 0,
  transition: 'all 0.15s',
  display: 'flex',
  flexDirection: 'column',
},
channelTileImg: {
  width: '100%',
  paddingTop: '56.25%',  // 16:9
  background: '#000',
  position: 'relative',
  overflow: 'hidden',
},
channelTileBadge: {
  position: 'absolute',
  top: 4, left: 4,
  background: 'rgba(0,0,0,0.7)',
  color: '#fff',
  padding: '2px 6px',
  borderRadius: 3,
  fontSize: 10,
  fontWeight: 600,
},
channelTileLoading: {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9ca3af',
},
channelTileError: {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9ca3af',
  gap: 2,
},
channelTileLabel: {
  padding: '6px 8px',
  fontSize: 12,
  fontWeight: 500,
  color: '#374151',
  textAlign: 'center',
  borderTop: '1px solid #f3f4f6',
},




};

export default LibraryPage;
